// ── Workflow Builder Guided mode — component picker + suggested-field data ────
//
// Provider/field vocabulary is pulled from what the Ops Center Mockup's node
// drawers actually read off `configuration.parameters` (see KycDrawer.tsx,
// AmlDrawer.tsx, KybDrawer.tsx, FraudDrawer.tsx) and from real sample workflow
// JSON, so a task built here looks identical to one hand-configured in the
// drag-and-drop editor.
import {
  ListChecks, ScanFace, UserCheck, ShieldAlert, Building2, Smartphone, type LucideIcon,
} from 'lucide-react'
import type { StepName, Task, StepTask, BranchTask } from '@features/dashboards/components/OpsCenterMockup/types'

export type WorkflowComponentType = Exclude<StepName, 'DECISION' | 'TOGGLEMONITORING'>

export interface ProviderOption {
  value: string
  label: string
}

export interface WorkflowComponentDef {
  type: WorkflowComponentType
  label: string
  description: string
  icon: LucideIcon
  color: string
  tint: string
  /** Empty for component types with no real provider concept (e.g. Matchlist). */
  providerOptions: ProviderOption[]
  providerFieldLabel: string
  secondaryFieldLabel: string
  secondaryFieldHint: string
  /** Quick-pick suggestions shown as chips — still a free-text field underneath. */
  secondarySuggestions: string[]
}

export const WORKFLOW_COMPONENTS: WorkflowComponentDef[] = [
  {
    type: 'KYC',
    label: 'KYC — Identity & Background Check',
    description: 'Confirms who the applicant is against a data source, then checks their background.',
    icon: UserCheck,
    color: '#15803d',
    tint: 'rgba(22,163,74,0.1)',
    providerOptions: [
      { value: 'manual', label: 'Manual' },
      { value: 'dvs', label: 'DVS' },
      { value: 'experian-cdh-v2', label: 'Experian-CDH-v2' },
      { value: 'equifax', label: 'Equifax' },
      { value: 'superchoice', label: 'Superchoice' },
    ],
    providerFieldLabel: 'Provider',
    secondaryFieldLabel: 'Ruleset name',
    secondaryFieldHint: 'Which document/verification ruleset this step runs.',
    secondarySuggestions: ['gov_id_only', 'two_plus', 'two_plus_gov_id'],
  },
  {
    type: 'IDV',
    label: 'IDV — Identity Verification & Biometrics',
    description: 'Document capture, face match and liveness — via Hosted OneSDK.',
    icon: ScanFace,
    color: '#1d4ed8',
    tint: 'rgba(37,99,235,0.1)',
    providerOptions: [
      { value: 'Daon', label: 'Daon' },
      { value: 'Incode', label: 'Incode' },
      { value: 'Onfido', label: 'Onfido' },
      { value: 'Sumsub', label: 'Sumsub' },
    ],
    providerFieldLabel: 'Provider',
    secondaryFieldLabel: 'Check type',
    secondaryFieldHint: 'What the applicant needs to complete.',
    secondarySuggestions: ['Document', 'Biometric', 'Full Bio'],
  },
  {
    type: 'AML',
    label: 'AML — Sanctions & PEP Screening',
    description: 'Screens the applicant against sanctions, PEP and adverse media lists.',
    icon: ShieldAlert,
    color: '#7c3aed',
    tint: 'rgba(124,58,237,0.1)',
    providerOptions: [
      { value: 'complyadvantage', label: 'ComplyAdvantage' },
      { value: 'refinitiv', label: 'Refinitiv' },
    ],
    providerFieldLabel: 'Provider',
    secondaryFieldLabel: 'Screening type',
    secondaryFieldHint: 'Which profile this step screens against.',
    secondarySuggestions: ['pep', 'pep_media'],
  },
  {
    type: 'KYB',
    label: 'KYB — Business Verification',
    description: 'Business registry lookup, UBO identification and entity screening.',
    icon: Building2,
    color: '#0f766e',
    tint: 'rgba(13,148,136,0.1)',
    providerOptions: [
      { value: 'abr', label: 'ABR' },
      { value: 'creditorwatch', label: 'CreditorWatch' },
      { value: 'kyckr', label: 'Kyckr' },
    ],
    providerFieldLabel: 'Provider',
    secondaryFieldLabel: 'Check profile',
    secondaryFieldHint: 'Which UBO/ownership profile this step applies.',
    secondarySuggestions: ['org_check', 'two_plus_pep_media'],
  },
  {
    type: 'FRAUD',
    label: 'Fraud & Device Risk',
    description: 'Device fingerprinting, IP risk scoring, email & phone risk.',
    icon: Smartphone,
    color: '#c2410c',
    tint: 'rgba(234,88,12,0.1)',
    providerOptions: [
      { value: 'seon', label: 'SEON' },
    ],
    providerFieldLabel: 'Provider',
    secondaryFieldLabel: 'Refer threshold',
    secondaryFieldHint: 'Risk level that routes to manual review instead of auto-fail.',
    secondarySuggestions: ['LOW', 'MEDIUM', 'HIGH'],
  },
  {
    type: 'MATCHLIST',
    label: 'Matchlist / Blocklist Screening',
    description: 'Checks the applicant against a customer-maintained block/allow list.',
    icon: ListChecks,
    color: '#b91c1c',
    tint: 'rgba(220,38,38,0.1)',
    providerOptions: [],
    providerFieldLabel: 'List source',
    secondaryFieldLabel: 'Match threshold (%)',
    secondaryFieldHint: 'How close a match needs to be before it’s flagged.',
    secondarySuggestions: ['80', '90', '100'],
  },
]

/**
 * Builds `configuration.parameters` for a newly-added task, using the same
 * field names the node drawers already read (`providerName`/`rulesetName` for
 * KYC, `providerName`/`name` for AML & KYB) so a Guided-mode task is
 * indistinguishable from one built by hand in the drag-and-drop editor.
 */
export function buildComponentParameters(
  type: WorkflowComponentType,
  provider: string,
  secondary: string,
): Record<string, string> {
  switch (type) {
    case 'KYC':
      return { providerName: provider, rulesetName: secondary }
    case 'AML':
    case 'KYB':
      return { providerName: provider, name: secondary }
    case 'FRAUD':
      return { providerName: provider, referThreshold: secondary }
    case 'IDV':
      return { providerName: provider, checkType: secondary }
    case 'MATCHLIST':
      return { source: provider, matchThreshold: secondary }
    default:
      return {}
  }
}

function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`
}

function kycProviderLabel(provider: string): string {
  return WORKFLOW_COMPONENTS.find((c) => c.type === 'KYC')?.providerOptions.find((o) => o.value === provider)?.label ?? provider
}

/**
 * A real KYC cascade isn't one provider — it's a fallback chain (try DVS, if no
 * match try Experian, then Equifax, ...), each step followed by a Branch task
 * whose "Else" tasks hold the next attempt. This mirrors that exact shape (see
 * `aus-advanced2v-v3.json`'s KYC steps) so Workflow Builder's Guided mode can
 * produce a genuine cascade instead of a single flat KYC step.
 */
export function buildKycCascadeTasks(providers: string[], ruleset: string): Task[] {
  if (providers.length === 0) return []
  const [first, ...rest] = providers
  const label = kycProviderLabel(first)
  const step: StepTask = {
    id: uid(`step_kyc_${first}`),
    name: `KYC Check (${label})`,
    configuration: { type: 'STEP', name: 'KYC', parameters: { providerName: first, rulesetName: ruleset } },
  }
  if (rest.length === 0) return [step]
  const branch: BranchTask = {
    id: uid('branch_kyc_result'),
    name: `${label} Result`,
    configuration: { type: 'BRANCH', branches: [{ name: 'Pass', tasks: [] }, { name: 'Else', tasks: buildKycCascadeTasks(rest, ruleset) }] },
  }
  return [step, branch]
}
